import { useCallback } from "react";

import { LogoutOutlined } from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { Menu } from "antd";
import { showIntercomWindow } from "next-intercom";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { RVIcon, RVIconSizeOptions } from "@regal-voice/ui";
import { usePageAvailability } from "App/routes/Hooks/usePageAvilability/usePageAvailability";
import { AppPagePaths } from "App/routes/paths";
import { SettingsPaths } from "Pages/settings/paths";
import { logger as authLogger, logout } from "Services/AuthService";
import { getEmbedUiConfigByKey } from "Services/embed/CrossDomainMessenger";
import { useFlags } from "Services/FeatureFlagService";
import { updateAgent } from "Services/marketing-api/agents/mutations";
import { selectAgentActivity, selectWorkerSid } from "Services/state/agent/AgentInformationSlice";
import { setDidManuallyLogOut } from "Services/state/users/UserSlice";

import {
    AgentAvailabilityDropdown,
    MENU_ITEM_ARIA_LABEL_PREFIX,
} from "../AgentAvailabilityDropdown/AgentAvailabilityDropdown";
import AgentStatusDisplay from "./AgentStatusDisplay";

export function AgentStatusDropdown(): JSX.Element {
    const dispatch = useDispatch();
    const agentActivity = useSelector(selectAgentActivity);
    const [updateAgentMutation] = useMutation(updateAgent);
    const workerSid = useSelector(selectWorkerSid);
    const { newProductAlertUrl } = useFlags();
    const { showAgentSettings } = usePageAvailability();
    const shouldHideAgentSettingsWhenEmbedded = getEmbedUiConfigByKey("hideAgentSettings");
    const shouldActuallyShowAgentSettings = showAgentSettings && !shouldHideAgentSettingsWhenEmbedded;

    function handleOnStatusChange(status: string) {
        if (agentActivity.name !== status) {
            updateAgentMutation({
                variables: {
                    updateUserData: {
                        status,
                        twilioSid: workerSid,
                    },
                },
            });
        }
    }

    const manualLogoutHandler = useCallback(() => {
        authLogger.log("Logging out from status dropdown");
        dispatch(setDidManuallyLogOut(true));
        logout();
    }, [dispatch]);

    return (
        <AgentAvailabilityDropdown
            onStatusChange={handleOnStatusChange}
            currentAgentStatus={agentActivity.name}
            additionalMenuItems={
                <>
                    <Menu.Item
                        key="get-help"
                        icon={<RVIcon icon="chat-bubble" size={RVIconSizeOptions.SMALL} />}
                        onClick={showIntercomWindow}
                        aria-label={`${MENU_ITEM_ARIA_LABEL_PREFIX}get-help`}
                    >
                        Get Help
                    </Menu.Item>
                    <Menu.Item key="whats-new" icon={<RVIcon icon="megaphone" size={RVIconSizeOptions.SMALL} />}>
                        <a href={newProductAlertUrl} target="_blank" rel="noreferrer">
                            What&apos;s New
                        </a>
                    </Menu.Item>
                    {shouldActuallyShowAgentSettings && (
                        <Menu.Item
                            key="Settings"
                            icon={<RVIcon icon="settings" size={RVIconSizeOptions.SMALL} />}
                            aria-label={`${MENU_ITEM_ARIA_LABEL_PREFIX}settings`}
                        >
                            <Link to={`${AppPagePaths.agentDesktop}/${SettingsPaths.mySettings}`}>My Settings</Link>
                        </Menu.Item>
                    )}
                    <Menu.Divider />
                    <Menu.Item
                        key="logout"
                        icon={<LogoutOutlined />}
                        onClick={manualLogoutHandler}
                        aria-label={`${MENU_ITEM_ARIA_LABEL_PREFIX}logout`}
                    >
                        Log out
                    </Menu.Item>
                </>
            }
        >
            <span translate="no" style={{ cursor: "pointer" }}>
                <AgentStatusDisplay userActivityInformation={agentActivity} />
            </span>
        </AgentAvailabilityDropdown>
    );
}
